import React, { useState, useCallback, useEffect, useRef } from 'react';

export function useElementOverflow<T extends HTMLElement>(
  deps: React.DependencyList = []
): [boolean, (el: T | null) => void] {
  const elementRef = useRef<T | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  const checkOverflow = useCallback((el: T | null) => {
    if (!el) {
      setIsOverflow(false);
      return;
    }
    setIsOverflow(el.scrollWidth > el.clientWidth);
  }, []);

  const refCallback = useCallback(
    (el: T | null) => {
      if (resizeObserverRef.current && elementRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      elementRef.current = el;
      if (!el) {
        setIsOverflow(false);
        return;
      }
      checkOverflow(el);
      resizeObserverRef.current = new ResizeObserver(() => checkOverflow(el));
      resizeObserverRef.current.observe(el);
    },
    [checkOverflow]
  );

  useEffect(() => {
    return () => resizeObserverRef.current?.disconnect();
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      checkOverflow(elementRef.current);
    }
  }, deps);

  return [isOverflow, refCallback];
}
